<template>
  <el-popover
    trigger="click"
    placement="bottom-start"
    popper-class="fb-popover"
    :value="viewsOpen"
    @input="(v) => (viewsOpen = v)"
  >
    <div class="svm-views">
      <div class="svm-views__header">Saved views</div>
      <div class="svm-views__list">
        <div
          v-for="p in presets"
          :key="'svm-' + p.id"
          class="svm-views__item"
          :class="{ 'svm-views__item--active': appliedPresetId === p.id }"
        >
          <span class="svm-views__name" @click="applyPreset(p)">{{ p.name }}</span>
          <button
            type="button"
            class="svm-views__star"
            :class="{ 'svm-views__star--on': p.is_default }"
            :title="p.is_default ? 'Default view' : 'Set as default'"
            @click.stop="toggleDefault(p)"
          >
            <i class="tim-icons icon-shape-star"></i>
          </button>
          <button type="button" class="svm-views__del" title="Delete view" @click.stop="onDeletePreset(p)">
            <i class="tim-icons icon-trash-simple"></i>
          </button>
        </div>
        <div v-if="presets.length === 0" class="svm-views__empty">No saved views yet</div>
      </div>
      <div class="svm-views__save">
        <input
          v-model="newViewName"
          type="text"
          class="svm-input"
          placeholder="Save current as..."
          maxlength="100"
          @keyup.enter="onSavePreset"
        />
        <button type="button" class="svm-save-btn" :disabled="!newViewName.trim() || savingPreset" @click="onSavePreset">Save</button>
      </div>
      <button type="button" class="svm-views__manage" @click="onManageViews">
        <i class="tim-icons icon-settings-gear-63"></i>
        <span>Manage views</span>
      </button>
    </div>
    <button slot="reference" type="button" class="svm-chip" :class="{ 'svm-chip--active': appliedPresetId != null }">
      <span class="svm-chip__icon"><i class="tim-icons icon-book-bookmark"></i></span>
      <span class="svm-chip__label">{{ currentViewLabel }}</span>
      <i class="tim-icons icon-minimal-down svm-chip__chevron"></i>
    </button>
  </el-popover>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, getCurrentInstance } from 'vue'
import { useSavedFilters } from '@/composables/useSavedFilters'
import { openSavedViews } from '@/composables/useSavedViewsDrawer'
import type { SavedFilterRow } from '@/util/savedFiltersApi'

// Standalone saved-views chip for pages that do NOT use FilterBar (e.g. the bookings
// views). The page keeps its own filter UI; this only adds the views menu.
//
// Contract:
//   - bind :current-filters to the page's serializable filter state (the object
//     that gets stored in a saved view). This component deep-watches it and
//     debounce-saves the "last used" row automatically.
//   - pass :url-values / :url-has-filters so precedence can prefer a deep link.
//   - handle @apply(values): set the page's filter state from a chosen/restored view.
//   - handle @ready(): run the page's initial fetch (fires once after precedence
//     resolves, so the page should defer its first filter fetch until then).
const props = defineProps<{
  viewKey: string
  currentFilters: Record<string, any>
  urlValues?: Record<string, any> | null
  urlHasFilters?: boolean
}>()

const emit = defineEmits<{
  (e: 'apply', values: Record<string, any>): void
  (e: 'ready'): void
}>()

const saved = useSavedFilters(props.viewKey)
const presets = computed<SavedFilterRow[]>(() => saved.presets.value)

const viewsOpen = ref(false)
const savingPreset = ref(false)
const newViewName = ref('')
const appliedPresetId = ref<number | null>(null)

const currentViewLabel = computed(() => {
  if (appliedPresetId.value != null) {
    const p = presets.value.find((x) => x.id === appliedPresetId.value)
    if (p) return p.name
  }
  return 'Views'
})

const inst = getCurrentInstance()
const vm = inst ? (inst.proxy as any) : null
function readViewParam(): number | null {
  const v = vm?.$route?.query?.view
  if (v == null || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}
function stripViewParam() {
  const router = vm?.$router
  const route = vm?.$route
  if (!router || !route || !('view' in route.query)) return
  const q = { ...route.query }
  delete q.view
  router.replace({ query: q }).catch(() => {})
}

let ready = false
// Set right before we emit an apply so the deep-watch keeps appliedPresetId
// (a preset was applied) rather than treating the change as a manual edit.
let applyingPreset = false

function applyPreset(p: SavedFilterRow) {
  applyingPreset = true
  appliedPresetId.value = p.id
  viewsOpen.value = false
  emit('apply', p.filters)
}

async function onSavePreset() {
  const name = newViewName.value.trim()
  if (!name) return
  savingPreset.value = true
  try {
    const id = await saved.savePreset(name, props.currentFilters)
    if (id != null) appliedPresetId.value = id
    newViewName.value = ''
    viewsOpen.value = false
  } finally {
    savingPreset.value = false
  }
}

async function toggleDefault(p: SavedFilterRow) {
  await saved.setDefault(p.id, !p.is_default)
}

async function onDeletePreset(p: SavedFilterRow) {
  await saved.remove(p.id)
  if (appliedPresetId.value === p.id) appliedPresetId.value = null
}

function onManageViews() {
  viewsOpen.value = false
  openSavedViews()
}

onMounted(async () => {
  await saved.load()
  const presetId = readViewParam()
  const res = saved.resolve({ presetId, urlValues: props.urlValues, urlHasFilters: props.urlHasFilters })
  if (res.values) {
    applyingPreset = true
    emit('apply', res.values)
  }
  appliedPresetId.value = res.appliedPresetId
  saved.primeLastUsedBaseline(res.values || props.currentFilters)
  if (presetId != null) stripViewParam()
  ready = true
  emit('ready')
})

// Persist the page's current filters as "last used" (debounced) on every change,
// and drop the applied-preset label once the user edits away from it.
watch(
  () => props.currentFilters,
  (val) => {
    if (!ready) return
    if (applyingPreset) applyingPreset = false
    else appliedPresetId.value = null
    saved.saveLastUsed(val)
  },
  { deep: true },
)

// Apply a preset when ?view= appears while already mounted (opened from the drawer
// on the page you're already on).
watch(
  () => vm?.$route?.query?.view,
  (v) => {
    if (!ready || v == null || v === '') return
    const n = Number(v)
    if (!Number.isFinite(n)) return
    const preset = presets.value.find((p) => p.id === n)
    stripViewParam()
    if (preset) applyPreset(preset)
  },
)
</script>

<script lang="ts">
export default { name: 'SavedViewsMenu' }
</script>

<style scoped>
/* Matches the FilterBar chip (.fb-chip) exactly so saved-views chips are uniform. */
.svm-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 12px;
  font-size: 13px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.85);
  background: transparent;
  border: 1px dashed rgba(255, 255, 255, 0.25);
  border-radius: 16px;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;

  &:hover { border-color: rgba(255, 255, 255, 0.4); background: rgba(255, 255, 255, 0.04); }
  &--active { border-style: solid; border-color: rgba(225, 78, 202, 0.4); background: rgba(225, 78, 202, 0.07); }
}
.svm-chip__icon { color: rgba(255, 255, 255, 0.5); display: inline-flex; }
.svm-chip__icon i { font-size: 13px; }
.svm-chip__label { max-width: 140px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: 600; }
.svm-chip__chevron { font-size: 11px; color: #e14eca; flex-shrink: 0; }

.svm-views { padding: 14px 14px 12px; min-width: 240px; }
.svm-views__header { font-size: 14px; font-weight: 600; color: #fff; margin-bottom: 12px; }
.svm-views__list { max-height: 240px; overflow-y: auto; margin-bottom: 12px; }
.svm-views__item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 6px;
  cursor: pointer;

  &:hover { background: rgba(255, 255, 255, 0.06); }
  &--active { background: rgba(225, 78, 202, 0.12); }
}
.svm-views__name {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  color: #fff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.svm-views__star,
.svm-views__del {
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.4);

  i { font-size: 13px; }
  &:hover { background: rgba(255, 255, 255, 0.1); color: #fff; }
}
.svm-views__star--on { color: #e14eca; }
.svm-views__del:hover { color: #fd5d93; }
.svm-views__empty { font-size: 13px; color: rgba(255, 255, 255, 0.45); padding: 8px 10px; }
/* Joined input-group: input on the left, Save button attached on the right. */
.svm-views__save { display: flex; margin-bottom: 14px; }
.svm-input {
  flex: 1;
  min-width: 0;
  height: 36px;
  padding: 0 10px;
  font-size: 13px;
  color: #fff;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-right: none;
  border-radius: 6px 0 0 6px;
  outline: none;

  &:focus { border-color: #e14eca; }
  &::placeholder { color: rgba(255, 255, 255, 0.4); }
}
.svm-save-btn {
  flex-shrink: 0;
  height: 36px;
  padding: 0 14px;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  background: #e14eca;
  border: none;
  border-radius: 0 6px 6px 0;
  cursor: pointer;

  &:hover { background: #d23bb8; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}
.svm-views__manage {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 12px 10px 2px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.75);
  background: none;
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
  text-align: left;

  i { font-size: 14px; color: rgba(255, 255, 255, 0.5); }
  &:hover { color: #fff; i { color: #e14eca; } }
}
</style>

<style>
/* Light mode */
.white-content .svm-chip { color: rgba(0, 0, 0, 0.8); border-color: rgba(0, 0, 0, 0.2); }
.white-content .svm-chip:hover { border-color: rgba(0, 0, 0, 0.4); background: rgba(0, 0, 0, 0.03); }
.white-content .svm-chip__icon { color: rgba(0, 0, 0, 0.4); }
.white-content .svm-chip__chevron { color: rgba(0, 0, 0, 0.4); }
.white-content .svm-views__header { color: #1e1e2f; }
.white-content .svm-views__name { color: #1e1e2f; }
.white-content .svm-views__item:hover { background: rgba(0, 0, 0, 0.04); }
.white-content .svm-views__item--active { background: rgba(225, 78, 202, 0.1); }
.white-content .svm-views__star,
.white-content .svm-views__del { color: rgba(0, 0, 0, 0.4); }
.white-content .svm-views__star:hover,
.white-content .svm-views__del:hover { background: rgba(0, 0, 0, 0.06); color: #1e1e2f; }
.white-content .svm-views__star--on { color: #e14eca; }
.white-content .svm-input { color: #1e1e2f; background: rgba(0, 0, 0, 0.03); border-color: rgba(0, 0, 0, 0.15); }
.white-content .svm-views__manage { color: rgba(0, 0, 0, 0.7); border-top-color: rgba(0, 0, 0, 0.1); }
.white-content .svm-views__manage i { color: rgba(0, 0, 0, 0.4); }
</style>
